import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/useToast";
import { orderService, type BackendOrder } from "@/services";
import {
  Calendar,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  ShoppingCart,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function formatPrice(amount: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getOrderStatus(estado: string) {
  switch ((estado || "").toUpperCase()) {
    case "PAGADO":
      return {
        label: "Pagado",
        variant: "default" as const,
        className: "bg-green-100 text-green-800",
        icon: CheckCircle,
      };
    case "PENDIENTE":
      return {
        label: "Pendiente",
        variant: "secondary" as const,
        className: "bg-yellow-100 text-yellow-800",
        icon: Clock,
      };
    case "CANCELADO":
      return {
        label: "Cancelado",
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800",
        icon: AlertCircle,
      };
    default:
      return {
        label: estado || "Desconocido",
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
        icon: Package,
      };
  }
}

export default function OrderHistory() {
  const [orders, setOrders] = useState<BackendOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  async function loadOrders() {
    try {
      setIsLoading(true);
      setError(null);
      let ordersData: BackendOrder[] = [];
      try {
        ordersData = await orderService.getMyOrdersFromTransactions();
      } catch (e) {
        ordersData = await orderService.getBackendOrders();
      }
      setOrders(ordersData);
    } catch (err: any) {
      setError(err.message || "Error al cargar las órdenes");
      toast({
        title: "Error",
        description: "No se pudieron cargar las órdenes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const monthOptions = useMemo((): { value: string; label: string }[] => {
    const uniqueKeys = new Set<string>();
    orders.forEach((o) => {
      const d = new Date(o.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      uniqueKeys.add(key);
    });
    const sorted = Array.from(uniqueKeys).sort((a, b) => b.localeCompare(a));
    return sorted.map((key) => {
      const [y, m] = key.split("-");
      const d = new Date(Number(y), Number(m) - 1, 1);
      const labelRaw = d.toLocaleDateString("es-CL", { month: "long", year: "numeric" });
      const label = labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);
      return { value: key, label };
    });
  }, [orders]);

  const displayOrders = useMemo(() => {
    if (!selectedMonth) return orders;
    return orders.filter((o) => {
      const d = new Date(o.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return key === selectedMonth;
    });
  }, [orders, selectedMonth]);

  function handleViewOrderDetail(orderId: string) {
    navigate(`/dashboard/orders/${orderId}?context=purchase-history`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Cargando historial de compras...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-700 mb-2">Error al cargar órdenes</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={loadOrders} variant="outline">
          Intentar de nuevo
        </Button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-8">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">No hay compras realizadas</h3>
        <p className="text-muted-foreground mb-4">Realiza tu primera compra para ver tu historial aquí</p>
        <Button onClick={() => navigate("/store")} className="h-12 text-base">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Ir a la Tienda
        </Button>
      </div>
    );
  }

  if (displayOrders.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium flex items-center gap-2"><Calendar className="h-4 w-4" /> <span>Filtrar por mes</span></label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Todos los meses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos los meses</SelectItem>
                {monthOptions.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No hay compras en el mes seleccionado</h3>
          <p className="text-muted-foreground mb-4">Cambia el mes o limpia el filtro para ver otras compras</p>
          <Button onClick={() => setSelectedMonth("")} variant="outline" className="h-10">Limpiar filtro</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium flex items-center gap-2"><Calendar className="h-4 w-4" /> <span>Filtrar por mes</span></label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Todos los meses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todos los meses</SelectItem>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow key="orders-header-row">
              <TableHead>ID de Orden</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Productos</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayOrders.map((order) => {
              const status = getOrderStatus(order.estado);
              const StatusIcon = status.icon;
              return (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-sm">{order.id.slice(0, 8)}...</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{formatDate(order.createdAt)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span>{order.itemsCount} productos</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{formatPrice(order.total)}</TableCell>
                  <TableCell>
                    <Badge variant={status.variant} className={status.className}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button onClick={() => handleViewOrderDetail(order.id)} size="sm" variant="outline" className="h-8">
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-4">
        {displayOrders.map((order) => {
          const status = getOrderStatus(order.estado);
          const StatusIcon = status.icon;
          return (
            <Card key={order.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-sm">{order.id.slice(0, 8)}...</span>
                  </div>
                  <Badge variant={status.variant} className={status.className}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span>{order.itemsCount} productos</span>
                  </div>
                  <div className="text-lg font-semibold">{formatPrice(order.total)}</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleViewOrderDetail(order.id)} variant="outline" size="sm" className="flex-1 h-10">
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalle
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


